import React from 'react'
import { useQuery } from 'react-query/react'

import useLocation from 'react-use/lib/useLocation'
import useAxios from '@talentinc/gatsby-theme-ecom/hooks/useAxios'

interface Education {
  name: string | null
  start_year: number | null
  end_year: number | null
}

interface WorkExperience {
  name: string | null
  start_year: number | null
  end_year: number | null
}

export interface Match {
  match_id: string | null
  job_title: string | null
  job_employer_name: string | null
  match_date: string | null
  skill_status: string | null
  skill_num_found: string | null
  skill_num_missing: string | null
  fit: string | null
  experience_status: string | null
  experience_num_found: string | null
  experience_num_missing: string | null
  info_status: string | null
  info_num_found: string | null
  info_num_missing: string | null
  read_status: string | null
  read_num_found: string | null
  read_num_missing: string | null
  MatchPartnerName: string | null
  skills_positive: string[]
  skills_negative: string[]
  work_experience: WorkExperience[]
  education: Education[]
  page_count: string | null
}

interface SessionResponse {
  lead_session:
    | {
        lead_auth_token: string | null
        email: string | null
        uploaded_resume: boolean
        ordered_resume_checksums: string[]
        viewable_critique: boolean
      }
    | undefined
}

export interface MatchApiResponse extends SessionResponse {
  matches: Match[]
}

export interface MatchData extends SessionResponse {
  match: Match | undefined
}

export interface MatchDataReturns extends MatchData {
  isLoading: boolean
  isError: boolean
}

const defaultMatchData = Object.freeze<MatchDataReturns>({
  lead_session: {
    lead_auth_token: null,
    email: null,
    uploaded_resume: false,
    ordered_resume_checksums: [],
    viewable_critique: false,
  },
  match: {
    match_id: '',
    job_title: '',
    job_employer_name: '',
    match_date: '',
    skill_status: '',
    skill_num_found: '',
    skill_num_missing: '',
    fit: '',
    experience_status: '',
    experience_num_found: '',
    experience_num_missing: '',
    info_status: '',
    info_num_found: '',
    info_num_missing: '',
    read_status: '',
    read_num_found: '',
    read_num_missing: '',
    MatchPartnerName: '',
    skills_positive: [],
    skills_negative: [],
    work_experience: [
      {
        name: '',
        start_year: 0,
        end_year: 0,
      },
    ],
    education: [
      {
        name: '',
        start_year: 0,
        end_year: 0,
      },
    ],
    page_count: '',
  },
  isLoading: false,
  isError: false,
})

export const MatchDataContext =
  React.createContext<MatchDataReturns>(defaultMatchData)

const MatchDataProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const axios = useAxios()
  const { search } = useLocation()

  const {
    data: apiResponse,
    isLoading,
    isError,
  } = useQuery<MatchApiResponse>(
    ['matchData', search ?? ''],
    async () => {
      const url = '/api/v2/match/landing-page'

      const params = new URLSearchParams(search?.substring(1) ?? '')

      if (document.referrer) {
        params.set('rf', document.referrer)
      }

      return axios.get(url, { params }).then((resp) => resp.data)
    },
    { staleTime: Infinity }
  )

  const data = {
    lead_session: apiResponse?.lead_session,
    match: apiResponse?.matches?.[0],
  }

  return (
    <MatchDataContext.Provider value={{ ...data, isLoading, isError }}>
      {children}
    </MatchDataContext.Provider>
  )
}

export default MatchDataProvider
